import {
    Box,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useRef, useState } from "react";

import { ds } from "../theme/ds";
import { chartPalette, colors } from "../theme/tokens";
import type { WidgetProps } from "./types";

// Interactive fulfillment-network map (2026-09-24, Tvilum pitch). Plain
// Leaflet inside a MUI shell. A provider list sits left of the map: a node
// count (2, 3 or 4) at its head, then every 3PL provider with the share of
// orders its best network of that size reaches; clicking one shows its
// buildings and selects that network (Bill, 2026-09-24: a list, not a
// dropdown). Clicks on buildings add or remove nodes. The preset row above
// the map (today's building, the optimal free placements, an empty canvas)
// and the candidate-metro layer came out on 2026-09-28 (Bill: not needed);
// the stored `presets` and `metros` stay in the schema and are not read.
// Five tiles under the map recompute from the points stored
// on the widget: reach within 300 and 600 miles, average miles, Prime
// two-day coverage against Amazon's Seller Fulfilled Prime target for the
// item's size tier, and the node count. Ported from the hub's
// center-of-gravity report.

type Node = { lat: number; lon: number; name: string };

const TILES =
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const ATTR = "Tiles &copy; Esri, HERE, Garmin, OpenStreetMap contributors";
const EARTH_MILES = 3958.8;
const KS = ["k2", "k3", "k4"] as const;
type K = (typeof KS)[number];

function miles(a: number, b: number, c: number, d: number) {
    const t = Math.PI / 180;
    const la1 = a * t;
    const lo1 = b * t;
    const la2 = c * t;
    const lo2 = d * t;
    const h =
        Math.sin((la2 - la1) / 2) ** 2 +
        Math.cos(la1) * Math.cos(la2) * Math.sin((lo2 - lo1) / 2) ** 2;
    return 2 * EARTH_MILES * Math.asin(Math.sqrt(h));
}

function sameNode(a: Node, b: Node) {
    return Math.abs(a.lat - b.lat) < 1e-3 && Math.abs(a.lon - b.lon) < 1e-3;
}

export function NetworkMap({ widget }: WidgetProps<"network_map">) {
    const { title, caption, source, unit = "orders" } = widget.props;
    const { points, total, baseline, centerOfMass, providers, sfp } = widget.data;

    const [nodes, setNodes] = useState<Node[]>(baseline);
    const [providerId, setProviderId] = useState<string>("");
    const [k, setK] = useState<K>("k2");

    const mapEl = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const layersRef = useRef<{
        dests: L.LayerGroup;
        rings: L.LayerGroup;
        facilities: L.LayerGroup;
    } | null>(null);

    const provider = providers.find((p) => p.id === providerId);

    const stats = useMemo(() => {
        let w300 = 0;
        let w600 = 0;
        let sum = 0;
        for (const [la, lo, n] of points) {
            let d = Infinity;
            for (const s of nodes) d = Math.min(d, miles(s.lat, s.lon, la, lo));
            sum += d * n;
            if (d <= 300) w300 += n;
            if (d <= 600) w600 += n;
        }
        return { w300: (100 * w300) / total, w600: (100 * w600) / total, avg: sum / total };
    }, [nodes, points, total]);

    // Create the map once.
    useEffect(() => {
        if (!mapEl.current || mapRef.current) return;
        const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView([38.5, -96.5], 4);
        L.tileLayer(TILES, { attribution: ATTR, maxZoom: 12 }).addTo(map);
        map.createPane("dests").style.zIndex = "400";
        map.createPane("rings").style.zIndex = "420";
        map.createPane("facilities").style.zIndex = "450";
        const canvas = L.canvas({ pane: "dests", padding: 0.5 });
        const dests = L.layerGroup().addTo(map);
        for (const [la, lo, n] of points) {
            L.circleMarker([la, lo], {
                pane: "dests",
                renderer: canvas,
                radius: Math.min(2 + Math.sqrt(n) * 1.2, 9),
                color: chartPalette[0],
                weight: 0.5,
                fillColor: chartPalette[0],
                fillOpacity: 0.5,
            })
                .bindTooltip(`${n} ${unit}`)
                .addTo(dests);
        }
        mapRef.current = map;
        layersRef.current = {
            dests,
            rings: L.layerGroup().addTo(map),
            facilities: L.layerGroup().addTo(map),
        };
        return () => {
            map.remove();
            mapRef.current = null;
            layersRef.current = null;
        };
    }, [points, unit]);

    // Redraw facilities and rings whenever the selection or the visible set changes.
    useEffect(() => {
        const layers = layersRef.current;
        if (!layers) return;
        layers.facilities.clearLayers();
        layers.rings.clearLayers();

        const toggle = (node: Node) => {
            setNodes((cur) =>
                cur.some((n) => sameNode(n, node))
                    ? cur.filter((n) => !sameNode(n, node))
                    : [...cur, node],
            );
        };
        const draw = (node: Node, color: string, radius: number, clickable: boolean) => {
            const selected = nodes.some((n) => sameNode(n, node));
            const m = L.circleMarker([node.lat, node.lon], {
                pane: "facilities",
                radius: selected ? radius + 2 : radius,
                color: selected ? colors.navy : color,
                fillColor: selected ? "#ffffff" : color,
                fillOpacity: 0.95,
                weight: selected ? 3 : 2,
            }).bindTooltip(node.name);
            if (clickable) m.on("click", () => toggle(node));
            m.addTo(layers.facilities);
        };

        for (const b of baseline) draw(b, colors.navy, 8, true);
        if (centerOfMass) draw(centerOfMass, "#059669", 7, false);
        if (provider) for (const f of provider.facilities) draw(f, "#E8910C", 7, true);
        for (const n of nodes)
            if (
                !provider?.facilities.some((f) => sameNode(f, n)) &&
                !baseline.some((b) => sameNode(b, n))
            )
                draw(n, "#7C3AED", 7, true);
        for (const n of nodes) {
            for (const mi of [300, 600]) {
                L.circle([n.lat, n.lon], {
                    pane: "rings",
                    radius: mi * 1609.34,
                    color: colors.navy,
                    weight: 1,
                    dashArray: mi === 300 ? undefined : "4 6",
                    fill: false,
                    opacity: 0.6,
                }).addTo(layers.rings);
            }
        }
    }, [nodes, provider, baseline, centerOfMass]);

    const pickProvider = (id: string, kk: K = k) => {
        const p = providers.find((x) => x.id === id);
        if (!p) return;
        setProviderId(id);
        const best = p.best?.[kk] ?? p.best?.k3 ?? p.best?.k2;
        setNodes(best?.nodes ?? p.facilities);
    };
    const pickK = (kk: K) => {
        setK(kk);
        if (providerId) pickProvider(providerId, kk);
    };

    const pct = (v: number) => `${v.toFixed(0)}%`;
    const tiles: { label: string; value: string; note?: string }[] = [
        { label: `${unit} within 300 miles`, value: pct(stats.w300) },
        { label: `${unit} within 600 miles`, value: pct(stats.w600) },
        { label: "average miles to the customer", value: Math.round(stats.avg).toLocaleString() },
    ];
    if (sfp) {
        const met = stats.w600 >= sfp.twoDayRequiredPct;
        tiles.push({
            label: `Prime two-day coverage, ${sfp.tier}`,
            value: pct(stats.w600),
            note: `${met ? "meets" : "short of"} the ${sfp.twoDayRequiredPct}% Amazon asks for`,
        });
    }
    tiles.push({ label: "nodes in this network", value: String(nodes.length) });

    const listed = [...providers].sort(
        (a, b) => (b.best?.[k]?.within600 ?? -1) - (a.best?.[k]?.within600 ?? -1),
    );

    return (
        <Box>
            {title && (
                <Typography
                    component="div"
                    sx={{ fontSize: 16, fontWeight: 700, color: ds.navy, mb: 0.5 }}
                >
                    {title}
                </Typography>
            )}
            {caption && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.xs, color: ds.gray500, mb: 2, maxWidth: 760 }}
                >
                    {caption}
                </Typography>
            )}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                <Box
                    sx={{
                        width: { xs: "100%", md: 280 },
                        flexShrink: 0,
                        border: 1,
                        borderColor: colors.line,
                        borderRadius: "10px",
                        bgcolor: "background.paper",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: colors.line }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: ds.navy, mb: 1 }}>
                            3PL providers, best network of
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            fullWidth
                            value={k}
                            onChange={(_, v) => v && pickK(v)}
                        >
                            {KS.map((kk) => (
                                <ToggleButton key={kk} value={kk}>
                                    {kk.slice(1)} nodes
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                    <List dense disablePadding sx={{ overflowY: "auto", maxHeight: { md: 470 } }}>
                        {listed.map((p) => {
                            const best = p.best?.[k];
                            const selected = p.id === providerId;
                            const tag = p.recommended ? ", shortlisted" : "";
                            return (
                                <ListItemButton
                                    key={p.id}
                                    selected={selected}
                                    onClick={() => pickProvider(p.id)}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderBottom: 1,
                                        borderColor: colors.lineSoft,
                                        "&.Mui-selected": { bgcolor: ds.info.bg },
                                        "&.Mui-selected:hover": { bgcolor: ds.info.bg },
                                    }}
                                >
                                    <ListItemText
                                        primary={p.name}
                                        secondary={
                                            best
                                                ? `${best.within600}% within 600 mi, ${best.avgMiles.toLocaleString()} mi avg${tag}`
                                                : `${p.facilities.length} building${p.facilities.length === 1 ? "" : "s"} only${tag}`
                                        }
                                        primaryTypographyProps={{
                                            fontSize: 13,
                                            fontWeight: selected ? 700 : 600,
                                            color: ds.navy,
                                        }}
                                        secondaryTypographyProps={{
                                            fontSize: 12,
                                            color: ds.gray500,
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Box>
                <Box
                    ref={mapEl}
                    sx={{
                        // Stacked under md the stack is a column, where `flex: 1`
                        // becomes a zero flex-basis on the height and the map
                        // collapsed to 0px on phones (Bill, 2026-09-28).
                        flex: { xs: "0 0 auto", md: 1 },
                        minWidth: 0,
                        height: { xs: 380, md: 560 },
                        borderRadius: "10px",
                        border: 1,
                        borderColor: colors.line,
                        overflow: "hidden",
                        "& .leaflet-container": { fontFamily: "inherit" },
                    }}
                />
            </Stack>
            <Box
                sx={{
                    mt: 2,
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: {
                        xs: "repeat(2, 1fr)",
                        md: `repeat(${tiles.length}, 1fr)`,
                    },
                }}
            >
                {tiles.map((t) => (
                    <Box
                        key={t.label}
                        sx={{
                            border: 1,
                            borderColor: colors.line,
                            borderRadius: "10px",
                            px: 2,
                            py: 2,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "1.75rem",
                                fontWeight: 800,
                                color: colors.navy,
                                lineHeight: 1.1,
                            }}
                        >
                            {t.value}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                            {t.label}
                        </Typography>
                        {t.note && (
                            <Typography sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: 0.25 }}>
                                {t.note}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                {nodes.map((n) => (
                    <Chip
                        key={n.name}
                        label={n.name}
                        size="small"
                        onDelete={() => setNodes((cur) => cur.filter((x) => !sameNode(x, n)))}
                    />
                ))}
                {nodes.length === 0 && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Pick a provider, or click a building on the map to add it to the network.
                    </Typography>
                )}
            </Stack>
            <Typography component="div" sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: 1.5 }}>
                Source: {source}. Reach is measured in great-circle miles; road miles run 15 to 25%
                longer. A 600-mile ring is roughly two days by ground parcel.
                {sfp && ` ${sfp.source}`}
            </Typography>
        </Box>
    );
}
